'use client'

import { useState, useTransition, useEffect } from 'react'
import { X, Bell, Trash2 } from 'lucide-react'
import { createJobAlert, getJobAlerts, updateJobAlert, deleteJobAlert } from '../_actions/saved-job-actions'

interface JobAlertModalProps {
  isOpen: boolean
  onClose: () => void
  currentFilters: Record<string, unknown>
}

interface AlertItem {
  id: string
  name: string
  filters: Record<string, unknown>
  frequency: string
  enabled: boolean
  createdAt: string
}

export function JobAlertModal({ isOpen, onClose, currentFilters }: JobAlertModalProps) {
  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }
  const [name, setName] = useState('')
  const [frequency, setFrequency] = useState('daily')
  const [alerts, setAlerts] = useState<AlertItem[]>([])
  const [isPending, startTransition] = useTransition()
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) {
      startTransition(async () => {
        const result = await getJobAlerts()
        if (result.success && result.data) {
          setAlerts(result.data)
        }
      })
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleCreate = () => {
    if (!name.trim()) {
      setError('Please enter a name for this alert.')
      return
    }
    setError('')

    startTransition(async () => {
      const result = await createJobAlert({
        name: name.trim(),
        filters: currentFilters,
        frequency,
      })
      if (result.success && result.data) {
        setAlerts((prev) => [
          { id: result.data!.id, name: name.trim(), filters: currentFilters, frequency, enabled: true, createdAt: new Date().toISOString() },
          ...prev,
        ])
        setName('')
      } else if (!result.success) {
        setError(result.error)
      }
    })
  }

  const handleToggle = (alertId: string, enabled: boolean) => {
    setAlerts((prev) => prev.map((a) => a.id === alertId ? { ...a, enabled } : a))
    startTransition(async () => {
      await updateJobAlert(alertId, { enabled })
    })
  }

  const handleDelete = (alertId: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== alertId))
    startTransition(async () => {
      await deleteJobAlert(alertId)
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="mx-4 w-full max-w-md rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-6 shadow-[var(--shadow-lg)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-[var(--color-brand-indigo)]" />
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]" style={font}>
              Job Alerts
            </h2>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-[var(--color-bg-elevated)]">
            <X className="h-4 w-4 text-[var(--color-text-muted)]" />
          </button>
        </div>

        {/* Create form */}
        <div className="mb-4 space-y-3">
          <input
            type="text"
            placeholder="Alert name (e.g. 'Remote AI jobs')"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-page)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-brand-indigo)] focus:outline-none"
            style={font}
          />
          <div className="flex items-center gap-3">
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="flex-1 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-page)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-brand-indigo)] focus:outline-none"
              style={font}
            >
              {/* D7.QW.1 - "Instant" removed: the cron currently fires
                  on a daily cadence, so the option was a false promise.
                  Re-add when SSE / cron-every-15min fan-out lands. */}
              <option value="daily">Daily digest</option>
              <option value="weekly">Weekly digest</option>
            </select>
            <button
              type="button"
              onClick={handleCreate}
              disabled={isPending}
              className="rounded-[var(--radius-md)] bg-[var(--color-brand-indigo)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              style={font}
            >
              Create
            </button>
          </div>
          {error && (
            <p className="text-xs text-[var(--color-error)]" style={font}>{error}</p>
          )}
        </div>

        {/* Existing alerts */}
        {alerts.length > 0 && (
          <div className="border-t border-[var(--color-border-default)] pt-4">
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-[var(--color-text-muted)]" style={font}>
              Your Alerts
            </h3>
            <div className="space-y-2">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="flex items-center justify-between rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-page)] px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-[var(--color-text-primary)]" style={font}>
                      {alert.name}
                    </p>
                    <p className="text-xs text-[var(--color-text-muted)]" style={font}>
                      {alert.frequency}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleToggle(alert.id, !alert.enabled)}
                      className="rounded-full px-2 py-0.5 text-xs font-medium"
                      style={{
                        ...font,
                        backgroundColor: alert.enabled ? 'color-mix(in srgb, var(--color-status-success) 10%, transparent)' : 'var(--color-bg-elevated)',
                        color: alert.enabled ? 'var(--color-status-success)' : 'var(--color-text-muted)',
                      }}
                    >
                      {alert.enabled ? 'On' : 'Off'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(alert.id)}
                      className="rounded p-1 hover:bg-[var(--color-bg-elevated)]"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
